import React, { useCallback, useRef, useState } from 'react';
import { useFormContext, useWatch } from "react-hook-form";
import styles from '../styles.module.scss';
import AriaLabel from "../../../AriaComponents/AriaLabel";
import { usePolyglot } from "../../../../context/Polyglot";
import GroupEditMember from "./children/GroupEditMember";
import { useTypedSelector } from "../../../../redux/hooks";
import Icon from "../../../Icon";
import AriaButton from "../../../AriaComponents/AriaButton";
import AriaTextField from "../../../AriaComponents/AriaTextField";
import AddUsersDisplay from "./children/AddUsersDisplay";
import { useConversationContext } from '../../../../context/ChatContext/contextConvo';
import { ChatMemberFull, selectSortedMembersOnChat } from '../../../../redux/slices/chatNSlice';

const GroupMemberEdit: React.FC = () => {
    const { conversation } = useConversationContext();
    const { setValue } = useFormContext();

    const participants = useTypedSelector(state => selectSortedMembersOnChat(state, conversation.uuid));

    const adding: string[] = useWatch({ name: 'users_config.adding' }) ?? [];
    const removing: string[] = useWatch({ name: 'users_config.removing' }) ?? [];

    const [searchVal, setSearchVal] = useState('');
    const [showAddUsers, setShowAddUsers] = useState<boolean>(false);

    const { t } = usePolyglot();

    const inputRef = useRef(null);

    const handleRemoveExisting = useCallback((memberUuid: string) => {
        setValue('users_config.removing', [...removing, memberUuid], { shouldDirty: true });
    }, [removing, setValue]);

    const handleUnstageAdd = useCallback((memberUuid: string) => {
        setValue('users_config.adding', adding.filter(id => id !== memberUuid), { shouldDirty: true });
    }, [adding, setValue]);

    const handleAddMembers = useCallback(() => {
        const reconciledRemoving = removing.filter(id => !adding.includes(id));
        setValue('users_config.removing', reconciledRemoving, { shouldDirty: true });
        setShowAddUsers(false);
    }, [adding, removing, setValue]);

    const stagedMembers: ChatMemberFull[] = adding
        .filter(uuid => !participants.some(p => p.member === uuid))
        .map(uuid => ({
            uuid: '',
            member: uuid,
            external: false,
            joined_at: '',
            last_read_at: null,
            last_delivered_at: null,
            is_admin: false,
            is_creator: false,
            name: '',
        }));

    return (<>
        <AriaTextField
            value={searchVal}
            onChange={(e) => setSearchVal(e)}
            placeholder={t("phrases.search_members")}
            isSearch
            aria-labelledby={t("phrases.search_members")}
            ref={inputRef}
        />
        <div className={styles.channel_members}>
            <AriaLabel label={t("terms.contact", 2)} />
            <ul>
                <AriaButton
                    className={styles.group_member}
                    onClick={() => setShowAddUsers(!showAddUsers)}
                >
                    <Icon name='addUser' />
                    <p>
                        {t('actions.add_user', 2)}
                    </p>
                </AriaButton>
                {participants.map(member => {
                    if (!member.name.includes(searchVal)) return null;
                    if (removing.some(id => id === member.member)) return null;
                    return (
                        <GroupEditMember
                            key={`GEM_${member.member}`}
                            member={member}
                            onRemove={() => handleRemoveExisting(member.member)}
                        />
                    );
                })}
                {stagedMembers.map(member => (
                    <GroupEditMember
                        key={`GEM_STAGED_${member.member}`}
                        member={member}
                        onRemove={() => handleUnstageAdd(member.member)}
                    />
                ))}
            </ul>
        </div>
        {showAddUsers ? (
            <div className={styles.add_users_display}>
                <AddUsersDisplay
                    closeEditor={() => setShowAddUsers(false)}
                    onAddMembers={handleAddMembers}
                />
            </div>
        ) : null}
    </>);
};

export default GroupMemberEdit;
